import React from 'react';
import { List, Card, Tag, Space, Typography, Badge, Empty } from 'antd';
import {
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  BankOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type { AnnotationData } from '../../../types';

const { Paragraph } = Typography;

interface CaseListProps {
  cases: AnnotationData[];
  selectedCaseId?: string;
  onSelectCase: (caseData: AnnotationData) => void;
  loading?: boolean;
  viewMode?: 'list' | 'card';
}

const CaseList: React.FC<CaseListProps> = ({
  cases,
  selectedCaseId,
  onSelectCase,
  loading,
  viewMode = 'list',
}) => {
  if (cases.length === 0 && !loading) {
    return (
      <Empty
        description="暂无匹配案例"
        className="py-12"
      />
    );
  }

  return (
    <List
      loading={loading}
      dataSource={cases}
      renderItem={(caseData) => {
        const isSelected = caseData.docMeta.case_id === selectedCaseId;
        const questionCount = caseData.questionDetails.length;
        const replyCount = caseData.replyDetails.length;

        return (
          <List.Item
            className={`cursor-pointer transition-all ${
              isSelected
                ? 'bg-blue-50 border-l-4 border-primary'
                : 'hover:bg-gray-50 border-l-4 border-transparent'
            }`}
            onClick={() => onSelectCase(caseData)}
          >
            <Card
             
              className={`w-full ${isSelected ? 'shadow-md' : ''}`}
              title={
                <div className="flex items-center justify-between">
                  <Space>
                    <FileTextOutlined className="text-primary" />
                    <span className="font-mono text-sm">
                      {caseData.docMeta.case_id}
                    </span>
                    {isSelected && <Badge status="processing" />}
                  </Space>
                  <RightOutlined
                    className={`transition-transform ${
                      isSelected ? 'rotate-90 text-primary' : 'text-gray-400'
                    }`}
                  />
                </div>
              }
            >
              {/* 项目名称 */}
              <Paragraph
                ellipsis={{ rows: 2 }}
                className="font-medium text-sm mb-2"
              >
                {caseData.docMeta.project_name}
              </Paragraph>

              {/* 主体信息 */}
              <div className="text-xs text-gray-500 space-y-1 mb-3">
                <div className="flex items-center gap-1">
                  <BankOutlined />
                  <span className="truncate">
                    招标人: {caseData.docMeta.responder?.name}
                  </span>
                </div>
                {caseData.docMeta.questioner && (
                  <div className="flex items-center gap-1">
                    <UserOutlined />
                    <span className="truncate">
                      投标人: {caseData.docMeta.questioner.name}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <CalendarOutlined />
                  <span>{caseData.docMeta.issue_date}</span>
                </div>
              </div>

              {/* 统计信息 */}
              <div className="flex items-center gap-2 mb-3">
                <Tag color="blue">
                  {questionCount} 个质疑
                </Tag>
                <Tag color="green">
                  {replyCount} 个答复
                </Tag>
              </div>

              {/* 主题标签 */}
              <div className="flex flex-wrap gap-1">
                {Array.from(
                  new Set(caseData.questionDetails.map((q) => q.topic))
                )
                  .slice(0, 3)
                  .map((topic) => (
                    <Tag key={topic} color="orange">
                      {topic}
                    </Tag>
                  ))}
                {caseData.questionDetails.length > 3 && (
                  <Tag>+{caseData.questionDetails.length - 3}</Tag>
                )}
              </div>

              {/* 整体结论 */}
              {caseData.docMeta.overall_decision && (
                <div className="mt-3 pt-2 border-t">
                  <Tag
                    color={
                      caseData.docMeta.overall_decision === '维持原判'
                        ? 'red'
                        : caseData.docMeta.overall_decision === '部分采纳'
                        ? 'orange'
                        : caseData.docMeta.overall_decision === '完全采纳'
                        ? 'green'
                        : 'blue'
                    }
                  >
                    {caseData.docMeta.overall_decision}
                  </Tag>
                </div>
              )}
            </Card>
          </List.Item>
        );
      }}
    />
  );
};

export default CaseList;
